import { AuthorisedCapital } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/AuthorisedCapital/AuthorisedCapital.tsx';
import { CatchUpRights } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/CatchUpRights/CatchUpRights.tsx';
import { ExchangeRights } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/ExchangeRights/ExchangeRights.tsx';
import { IncomeRightsInterest } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/IncomeRights/IncomeRightsInterest.tsx';
import { IncomeRightsTracking } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/IncomeRightsTracking/IncomeRightsTracking.tsx';
import { ParticipationThreshold } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/ParticipationThreshold/ParticipationThreshold.tsx';
import { RedemptionRights } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/RedemptionRights/RedemptionRights.tsx';
import { RelativeRank } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import { SecurityFeature as SECURITY_FEATURES } from '@dealstack/shared/lib/common/securities/securityFeature';
import { SecurityFeaturesButtons } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/SecurityFeaturesButtons/SecurityFeaturesButtons.tsx';
import {
	DataSecurityWithAllFeaturesAndRanksTct,
	IncomeType,
} from '@dealstack/shared/lib/generated';
import { memo, useMemo, useState } from 'react';

import { Ratcheting } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/Ratcheting.tsx';
import { useSecurityRankingController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/useSecurityRankingController.tsx';
import { LiquidationPreference } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/LiquidationPreference/LiquidationPreference.tsx';
import { getSecurityLiquidationPreference } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { useQuery } from '@tanstack/react-query';

interface Props {
	security: DataSecurityWithAllFeaturesAndRanksTct | null;
	refreshDetails?: (securityId: string) => void;
	hasEditorPermission?: boolean;
}

const SecurityFeatures = ({ security, refreshDetails, hasEditorPermission }: Props) => {
	const [newFeatureRows, setNewFeatureRows] = useState({
		[SECURITY_FEATURES.AUTHORISED_CAPITAL]: false,
		[SECURITY_FEATURES.INCOME_RIGHTS_INTEREST]: false,
		[SECURITY_FEATURES.INCOME_RIGHTS_TRACKING]: false,
		[SECURITY_FEATURES.REDEMPTION_RIGHTS]: false,
		[SECURITY_FEATURES.CATCH_UP_RIGHTS]: false,
		[SECURITY_FEATURES.EXCHANGE_RIGHTS]: false,
		[SECURITY_FEATURES.LIQUIDATION_PREFERENCE]: false,
		[SECURITY_FEATURES.PARTICIPATION_THRESHOLD]: false,
		[SECURITY_FEATURES.RATCHETING]: false,
	});

	const { personRanks } = useSecurityRankingController(security?.issuerId);

	// LP is not part of the security HTTP response, so its current value is fetched here and passed down.
	const { data: liquidationPreference, refetch: refetchLiquidationPreference } = useQuery({
		queryKey: ['securityLiquidationPreference', security?.id],
		queryFn: () => getSecurityLiquidationPreference(security?.id ?? ''),
		enabled: !!security?.id,
	});

	const relativeRanksMap = useMemo(() => {
		if (!personRanks) {
			return new Map<string, RelativeRank>();
		}

		return personRanks
			.sort((a, b) => b.sortOrder - a.sortOrder)
			.reduce<Map<string, RelativeRank>>((map, rank, idx) => {
				map.set(rank.id, { ...rank, relativeSortOrder: idx + 1 });

				return map;
			}, new Map<string, RelativeRank>());
	}, [personRanks]);

	const showIncomeRightsInterest =
		security?.securityInterestDefinition ||
		newFeatureRows[SECURITY_FEATURES.INCOME_RIGHTS_INTEREST];
	const showIncomeRightsTracking =
		security?.tracking?.length ||
		security?.incomeType === IncomeType.TRACKING ||
		newFeatureRows[SECURITY_FEATURES.INCOME_RIGHTS_TRACKING];

	const removeNewFeatureRow = (key: SECURITY_FEATURES) => {
		if (!newFeatureRows[key]) {
			return;
		}

		setNewFeatureRows((prevState) => ({
			...prevState,
			[key]: false,
		}));
	};

	return (
		<>
			{(security?.authorisedCapital ||
				newFeatureRows[SECURITY_FEATURES.AUTHORISED_CAPITAL]) && (
				<AuthorisedCapital
					security={security}
					refreshDetails={(securityId) => {
						refreshDetails?.(securityId);
						removeNewFeatureRow(SECURITY_FEATURES.AUTHORISED_CAPITAL);
					}}
					onCancel={() => removeNewFeatureRow(SECURITY_FEATURES.AUTHORISED_CAPITAL)}
					hasEditorPermission={hasEditorPermission}
				/>
			)}

			{showIncomeRightsInterest && (
				<IncomeRightsInterest
					security={security}
					refreshDetails={(securityId) => {
						refreshDetails?.(securityId);
						removeNewFeatureRow(SECURITY_FEATURES.INCOME_RIGHTS_INTEREST);
					}}
					onCancel={() => removeNewFeatureRow(SECURITY_FEATURES.INCOME_RIGHTS_INTEREST)}
					hasEditorPermission={hasEditorPermission}
					relativeRanksMap={relativeRanksMap}
				/>
			)}

			{showIncomeRightsTracking && (
				<IncomeRightsTracking
					security={security}
					refreshDetails={(securityId) => {
						refreshDetails?.(securityId);
						removeNewFeatureRow(SECURITY_FEATURES.INCOME_RIGHTS_TRACKING);
					}}
					onCancel={() => removeNewFeatureRow(SECURITY_FEATURES.INCOME_RIGHTS_TRACKING)}
					hasEditorPermission={hasEditorPermission}
				/>
			)}

			{(security?.securityRedemptionRights ||
				newFeatureRows[SECURITY_FEATURES.REDEMPTION_RIGHTS]) && (
				<RedemptionRights
					security={security}
					refreshDetails={(securityId) => {
						refreshDetails?.(securityId);
						removeNewFeatureRow(SECURITY_FEATURES.REDEMPTION_RIGHTS);
					}}
					onCancel={() => removeNewFeatureRow(SECURITY_FEATURES.REDEMPTION_RIGHTS)}
					hasEditorPermission={hasEditorPermission}
				/>
			)}

			{(security?.securityCatchUpRights ||
				newFeatureRows[SECURITY_FEATURES.CATCH_UP_RIGHTS]) && (
				<CatchUpRights
					security={security}
					refreshDetails={(securityId) => {
						refreshDetails?.(securityId);
						removeNewFeatureRow(SECURITY_FEATURES.CATCH_UP_RIGHTS);
					}}
					onCancel={() => removeNewFeatureRow(SECURITY_FEATURES.CATCH_UP_RIGHTS)}
					hasEditorPermission={hasEditorPermission}
					relativeRanksMap={relativeRanksMap}
				/>
			)}

			{(security?.securityExchangeRights ||
				newFeatureRows[SECURITY_FEATURES.EXCHANGE_RIGHTS]) && (
				<ExchangeRights
					security={security}
					refreshDetails={(securityId) => {
						refreshDetails?.(securityId);
						removeNewFeatureRow(SECURITY_FEATURES.EXCHANGE_RIGHTS);
					}}
					onCancel={() => removeNewFeatureRow(SECURITY_FEATURES.EXCHANGE_RIGHTS)}
					hasEditorPermission={hasEditorPermission}
					relativeRanksMap={relativeRanksMap}
				/>
			)}

			{(liquidationPreference ||
				newFeatureRows[SECURITY_FEATURES.LIQUIDATION_PREFERENCE]) && (
				<LiquidationPreference
					security={security}
					liquidationPreference={liquidationPreference ?? null}
					refetchLiquidationPreference={refetchLiquidationPreference}
					refreshDetails={(securityId) => {
						refreshDetails?.(securityId);
						removeNewFeatureRow(SECURITY_FEATURES.LIQUIDATION_PREFERENCE);
					}}
					onCancel={() => removeNewFeatureRow(SECURITY_FEATURES.LIQUIDATION_PREFERENCE)}
					hasEditorPermission={hasEditorPermission}
					relativeRanksMap={relativeRanksMap}
				/>
			)}

			{(!!security?.residueParticipationThreshold ||
				newFeatureRows[SECURITY_FEATURES.PARTICIPATION_THRESHOLD]) && (
				<ParticipationThreshold
					security={security}
					refreshDetails={(securityId) => {
						refreshDetails?.(securityId);
						removeNewFeatureRow(SECURITY_FEATURES.PARTICIPATION_THRESHOLD);
					}}
					onCancel={() => removeNewFeatureRow(SECURITY_FEATURES.PARTICIPATION_THRESHOLD)}
					hasEditorPermission={hasEditorPermission}
					relativeRanksMap={relativeRanksMap}
				/>
			)}

			{(security?.residueRights || newFeatureRows[SECURITY_FEATURES.RATCHETING]) && (
				<Ratcheting
					security={security}
					isNewFeature={newFeatureRows[SECURITY_FEATURES.RATCHETING]}
					refreshDetails={(securityId) => {
						refreshDetails?.(securityId);
						removeNewFeatureRow(SECURITY_FEATURES.RATCHETING);
					}}
					onCancel={() => removeNewFeatureRow(SECURITY_FEATURES.RATCHETING)}
					hasEditorPermission={hasEditorPermission}
				/>
			)}

			<SecurityFeaturesButtons
				security={security}
				hasEditorPermission={hasEditorPermission}
				newFeatureRows={newFeatureRows}
				setNewFeatureRows={setNewFeatureRows}
				refreshDetails={refreshDetails}
			/>
		</>
	);
};

const Memoized = memo(SecurityFeatures);

export { Memoized as SecurityFeatures };
